import {
  EQUIPMENT_COUNT_POLICY_VERSION,
  type EquipmentLimitAttempt,
  type EquipmentLimitOperation,
  type EquipmentMutationSource,
} from "./equipment-limit";
import type { Category } from "./types";

const STORAGE_KEY = "crimp:equipment-limit-auth-handoff:v1";
const MAX_HANDOFF_AGE_MS = 30 * 60 * 1000;

export type EquipmentLimitAttemptScope =
  | { kind: "cloud"; id: string }
  | { kind: "local"; id: string }
  | { kind: "resource_example" };

type StoredHandoff = {
  policy: typeof EQUIPMENT_COUNT_POLICY_VERSION;
  attempt: EquipmentLimitAttempt;
  scope: EquipmentLimitAttemptScope;
  handoffAt: number;
};

type EquipmentLimitAuthHandoffMatch = {
  checkoutAttemptId: string;
  scope?: EquipmentLimitAttemptScope;
};

const SOURCES = new Set<EquipmentMutationSource>([
  "palette_click",
  "mobile_palette",
  "palette_drag",
  "custom_component",
  "ai_component",
  "component_arrival",
  "starter_template",
  "resource_example",
  "duplicate",
  "paste",
  "replace",
  "customize",
  "revert",
  "ai_edit",
  "ai_system",
  "undo",
  "redo",
  "unknown",
]);
const OPERATIONS = new Set<EquipmentLimitOperation>([
  "add",
  "remove",
  "duplicate",
  "paste",
  "replace",
  "generate",
  "undo",
  "redo",
]);
const CATEGORIES = new Set<Category | "unknown">([
  "battery",
  "charger",
  "solar",
  "source",
  "engine",
  "distribution",
  "load",
  "monitor",
  "unknown",
]);

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    // Magic links often open in a new tab, so this follows the existing auth
    // handoff pattern and uses bounded localStorage rather than sessionStorage.
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function nonNegativeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isAttempt(value: unknown, now: number): value is EquipmentLimitAttempt {
  if (!value || typeof value !== "object") return false;
  const attempt = value as Partial<EquipmentLimitAttempt>;
  return (
    typeof attempt.checkoutAttemptId === "string" &&
    attempt.checkoutAttemptId.length >= 16 &&
    typeof attempt.mutationId === "string" &&
    attempt.mutationId.length > 0 &&
    typeof attempt.source === "string" &&
    SOURCES.has(attempt.source as EquipmentMutationSource) &&
    typeof attempt.operation === "string" &&
    OPERATIONS.has(attempt.operation as EquipmentLimitOperation) &&
    nonNegativeNumber(attempt.currentCount) &&
    nonNegativeNumber(attempt.attemptedDelta) &&
    attempt.attemptedDelta > 0 &&
    nonNegativeNumber(attempt.wouldBeCount) &&
    (attempt.attemptedDefinitionId === null ||
      typeof attempt.attemptedDefinitionId === "string") &&
    typeof attempt.attemptedCategory === "string" &&
    CATEGORIES.has(attempt.attemptedCategory as Category | "unknown") &&
    nonNegativeNumber(attempt.createdAt) &&
    attempt.createdAt <= now + 60_000
  );
}

function isScope(value: unknown): value is EquipmentLimitAttemptScope {
  if (!value || typeof value !== "object") return false;
  const scope = value as Partial<EquipmentLimitAttemptScope> & { id?: unknown };
  if (scope.kind === "resource_example") return true;
  return (
    (scope.kind === "cloud" || scope.kind === "local") &&
    typeof scope.id === "string" &&
    scope.id.length > 0
  );
}

function scopesMatch(
  left: EquipmentLimitAttemptScope,
  right: EquipmentLimitAttemptScope,
): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === "resource_example" || right.kind === "resource_example") {
    return true;
  }
  return left.id === right.id;
}

function parseStoredHandoff(raw: string, now: number): StoredHandoff | null {
  const parsed = JSON.parse(raw) as Partial<StoredHandoff>;
  if (
    parsed.policy !== EQUIPMENT_COUNT_POLICY_VERSION ||
    !isAttempt(parsed.attempt, now) ||
    !isScope(parsed.scope) ||
    !nonNegativeNumber(parsed.handoffAt) ||
    parsed.handoffAt > now + 60_000 ||
    now - parsed.handoffAt > MAX_HANDOFF_AGE_MS
  ) {
    return null;
  }
  return parsed as StoredHandoff;
}

export function rememberEquipmentLimitAuthHandoff(
  attempt: EquipmentLimitAttempt,
  scope: EquipmentLimitAttemptScope,
  handoffAt = Date.now(),
): void {
  try {
    storage()?.setItem(
      STORAGE_KEY,
      JSON.stringify({
        policy: EQUIPMENT_COUNT_POLICY_VERSION,
        attempt,
        scope,
        handoffAt,
      } satisfies StoredHandoff),
    );
  } catch {
    // The cap still works when storage is unavailable; only redirect
    // attribution loses continuity.
  }
}

export function readEquipmentLimitAuthHandoff(
  scope: EquipmentLimitAttemptScope,
  now = Date.now(),
): EquipmentLimitAttempt | null {
  const target = storage();
  if (!target) return null;
  try {
    const raw = target.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseStoredHandoff(raw, now);
    if (!parsed) {
      target.removeItem(STORAGE_KEY);
      return null;
    }
    // Opening another project must not consume or display this project's
    // paywall. The matching canvas can still recover it later in the tab.
    if (!scopesMatch(parsed.scope, scope)) return null;
    return parsed.attempt;
  } catch {
    try {
      target.removeItem(STORAGE_KEY);
    } catch {
      // Ignore inaccessible storage.
    }
    return null;
  }
}

/** Remove only the journey that the caller owns. localStorage is shared by
 * every tab, so an unrelated project must not erase a pending magic-link
 * handoff created elsewhere. */
export function clearEquipmentLimitAuthHandoff(
  match: EquipmentLimitAuthHandoffMatch,
  now = Date.now(),
): void {
  const target = storage();
  if (!target) return;
  try {
    const raw = target.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = parseStoredHandoff(raw, now);
    if (!parsed) {
      target.removeItem(STORAGE_KEY);
      return;
    }
    if (parsed.attempt.checkoutAttemptId !== match.checkoutAttemptId) return;
    if (match.scope && !scopesMatch(parsed.scope, match.scope)) return;
    target.removeItem(STORAGE_KEY);
  } catch {
    // Ignore inaccessible storage.
  }
}
